import { Controller } from "@hotwired/stimulus"
import { logger } from "../logger"

// Connects to data-controller="report-autosave"
//
// Debounces form submission after edits with 5-second delay
// Automatically saves changes made to report columns, properties, etc.
//
// Usage:
//   form[
//     data-controller="report-autosave"
//     data-report-autosave-target="form"
//   ]
//
export default class extends Controller {
  static values = {
    delay: { type: Number, default: 500 }, // 0.5 ׳ seconds
    savedText: String,
    saveFailedText: String
  }
  
  static targets = ["form", "saveButton"]
  
  connect() {
    this.saveTimer = null
    this.isDirty = false
    this.revertTimer = null
    this.originalIconClasses = null // Store original icon classes once
    this.isSaving = false // CRITICAL: Prevent concurrent saves

    // CRITICAL: Check if this is a persisted report
    // Autosave only works for persisted reports (form action must include :report_id)
    // For new reports, user must save manually first
    this.isPersistedReport = this.checkIfPersisted()

    // Listen for the custom event
    this.boundScheduleHandler = (event) => {
      this.scheduleAutoSave(event)
    }
    this.element.addEventListener('report-autosave:schedule', this.boundScheduleHandler)
  }

  checkIfPersisted() {
    // Get the form element
    const form = this.element.tagName === 'FORM' ? this.element : this.element.querySelector('form')
    if (!form) {
      logger.warn("[Auto-Save] Form not found, assuming unpersisted")
      return false
    }

    // Check if form action includes a report ID
    // Persisted: /admin/settings/years/1/reports/123
    // Unpersisted: /admin/settings/years/1/reports
    const formAction = form.action
    const hasReportId = /\/reports\/\d+/.test(formAction)

    logger.log(`[Auto-Save] Form action: ${formAction}, Persisted: ${hasReportId}`)
    return hasReportId
  }
  
  disconnect() {
    this.clearTimer()
    this.clearRevertTimer()
    if (this.boundScheduleHandler) {
      this.element.removeEventListener('report-autosave:schedule', this.boundScheduleHandler)
    }
  }
  
  clearRevertTimer() {
    if (this.revertTimer) {
      clearTimeout(this.revertTimer)
      this.revertTimer = null
    }
  }
  
  // Called by other controllers when content changes
  scheduleAutoSave(event) {
    // CRITICAL: Only autosave for persisted reports
    // New reports must be saved manually first to create the record
    if (!this.isPersistedReport) {
      logger.log("[Auto-Save] Skipping autosave - report not yet persisted")
      return
    }

    this.isDirty = true
    this.clearTimer()

    this.saveTimer = setTimeout(() => {
      this.performAutoSave()
    }, this.delayValue)
  }
  
  // Called when user clicks save button
  triggerSave(event) {
    event.preventDefault()

    // CRITICAL: Only autosave for persisted reports
    if (!this.isPersistedReport) {
      logger.log("[Auto-Save] Skipping manual save - report not yet persisted")
      return
    }

    this.isDirty = true
    this.clearTimer()
    this.performAutoSave()
  }
  
  clearTimer() {
    if (this.saveTimer) {
      clearTimeout(this.saveTimer)
      this.saveTimer = null
    }
  }
  
  async performAutoSave() {
    if (!this.isDirty) {
      logger.log("[Auto-Save] Skipping save - no changes")
      return
    }

    // CRITICAL: Prevent concurrent saves to avoid race conditions
    if (this.isSaving) {
      logger.log("[Auto-Save] Save already in progress, scheduling retry...")
      // Reschedule this save to happen after the current one completes
      this.isDirty = true
      this.clearTimer()
      this.saveTimer = setTimeout(() => {
        this.performAutoSave()
      }, this.delayValue)
      return
    }

    this.isSaving = true
    logger.log("[Auto-Save] Performing auto-save")

    // The controller is attached to the form itself
    const form = this.element.tagName === 'FORM' ? this.element : this.element.querySelector('form')

    if (!form) {
      logger.error("[Auto-Save] Form not found")
      return
    }

    // CRITICAL: Recalculate positions based on DOM order before saving
    // This ensures the visual order is the source of truth
    this.recalculateColumnPositions()

    // CRITICAL: Capture which columns are marked for destruction BEFORE the save
    // This prevents a race condition where columns deleted DURING the save get
    // prematurely removed from the DOM before being sent to the server
    const columnsToCleanup = this.captureColumnsMarkedForDestruction(form)

    const formAction = form.action
    const formData = new FormData(form)

    // CRITICAL: Reset isDirty BEFORE the async fetch, not after.
    // If new changes come in during the fetch (e.g., user adds another column),
    // they'll set isDirty = true, and we won't accidentally overwrite it.
    this.isDirty = false

    try {
      const response = await fetch(formAction, {
        method: 'PATCH',
        body: formData,
        headers: {
          'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content,
          'Accept': 'application/json',
          // CRITICAL: Override Turbo's default Accept header
          // Without this, Rails processes the request as TURBO_STREAM instead of JSON
          'X-Requested-With': 'XMLHttpRequest',
          // Explicitly tell Turbo NOT to intercept this request
          'Turbo-Frame': ''
        },
        credentials: 'same-origin'
      })
      
      if (response.ok) {
        logger.log("[Auto-Save] Saved successfully")
        // Note: isDirty was reset before fetch started, don't reset here
        // or we'll overwrite any changes that came in during the save
        this.showSaveIndicator()

        // CRITICAL: Update column IDs for newly created columns
        const data = await response.json()
        this.updateNewColumnIds(data)

        // CRITICAL: Remove elements marked for destruction from DOM
        // This prevents subsequent saves from trying to destroy already-deleted records
        // Only cleanup columns that were captured BEFORE the save started
        this.cleanupDestroyedRecords(columnsToCleanup)
      } else {
        logger.error("[Auto-Save] Save failed:", response.status)
        this.showErrorIndicator()

        // CRITICAL: On 404, clear dirty flag to prevent infinite retry loop
        // 404 means the resource doesn't exist (likely a stale column reference)
        if (response.status === 404) {
          logger.error("[Auto-Save] 404 error - clearing dirty flag to prevent retry loop")
          this.isDirty = false
          this.showErrorMessages({ base: ['שגיאה 404: המשאב לא נמצא. רענן את הדף.'] })
        } else {
          // CRITICAL: Mark dirty to trigger retry on non-404 errors
          // This ensures the data in the failed request gets retried
          // (the form hasn't changed, so retry will send the same data)
          this.isDirty = true

          // Try to parse error messages from JSON response
          try {
            const errorData = await response.json()
            this.showErrorMessages(errorData)
          } catch (e) {
            // If JSON parsing fails, show generic error
            this.showErrorMessages({ base: [`שגיאה בשמירה (${response.status})`] })
          }
        }
      }
    } catch (error) {
      logger.error("[Auto-Save] Save error:", error)
      this.showErrorIndicator()
      this.showErrorMessages({ base: ['שגיאת רשת - נסה שוב'] })

      // CRITICAL: Mark dirty to trigger retry on network errors
      // This ensures the data in the failed request gets retried
      this.isDirty = true
    } finally {
      // CRITICAL: Always reset the saving flag, even if there's an error
      this.isSaving = false

      // If changes occurred during the save, schedule another save
      if (this.isDirty) {
        logger.log("[Auto-Save] Changes detected during save, scheduling another save...")
        this.clearTimer()
        this.saveTimer = setTimeout(() => {
          this.performAutoSave()
        }, this.delayValue)
      }
    }
  }
  
  updateNewColumnIds(data) {
    // When new columns are saved, server assigns database IDs
    // Update the form to include these IDs so next save is UPDATE not INSERT
    if (!data.column_id_mappings) {
      return
    }
    
    Object.entries(data.column_id_mappings).forEach(([tempId, dbId]) => {
      const columnElement = document.querySelector(`[data-column-id="${tempId}"]`)
      if (columnElement) {
        // Check if ID field already exists (shouldn't happen, but be safe)
        const existingIdField = columnElement.querySelector('input[name*="[id]"]')
        if (existingIdField) {
          existingIdField.value = dbId
          logger.log(`[Auto-Save] Updated existing ID field for column ${tempId} to ${dbId}`)
        } else {
          // Add hidden ID field to mark as persisted
          const sheetIndex = this.getSheetIndex(columnElement)
          const idInput = document.createElement('input')
          idInput.type = 'hidden'
          idInput.name = `report[report_sheets_attributes][${sheetIndex}][report_columns_attributes][${tempId}][id]`
          idInput.value = dbId
          columnElement.appendChild(idInput)
          
          logger.log(`[Auto-Save] Added ID field for column ${tempId} with database ID ${dbId}`)
        }
      }
    })
  }
  
  getSheetIndex(columnElement) {
    // Extract sheet index from an existing field name
    const input = columnElement.querySelector('input[name*="report_sheets_attributes"]')
    if (input) {
      const match = input.name.match(/\[report_sheets_attributes\]\[(\d+)\]/)
      if (match) {
        return match[1]
      }
    }
    // Fallback to 0
    return '0'
  }
  
  showSaveIndicator() {
    // Show green checkmark in save button icon
    if (!this.hasSaveButtonTarget) return

    const button = this.saveButtonTarget
    const icon = button.querySelector('i')

    if (icon) {
      // Store original classes ONCE (before first change)
      if (!this.originalIconClasses) {
        this.originalIconClasses = icon.className
      }

      // Change to green checkmark
      icon.className = 'fa fa-check text-success'
      button.classList.add('saved')

      // Revert back to save icon after 2 seconds
      this.clearRevertTimer()
      this.revertTimer = setTimeout(() => {
        icon.className = this.originalIconClasses
        button.classList.remove('saved')
      }, 2000)
    }
  }
  
  showErrorIndicator() {
    // Show red X in save button icon
    if (!this.hasSaveButtonTarget) return

    const button = this.saveButtonTarget
    const icon = button.querySelector('i')

    if (icon) {
      // Store original classes ONCE (before first change)
      if (!this.originalIconClasses) {
        this.originalIconClasses = icon.className
      }

      // Change to red X
      icon.className = 'fa fa-times text-danger'

      // Revert back to save icon after 3 seconds
      this.clearRevertTimer()
      this.revertTimer = setTimeout(() => {
        icon.className = this.originalIconClasses
      }, 3000)
    }
  }

  showErrorMessages(errors) {
    // Convert Rails errors object to user-friendly messages
    // errors format: { field_name: ["error1", "error2"], base: ["error"] }
    const messages = new Set() // Use Set to avoid duplicates

    for (const [field, fieldErrors] of Object.entries(errors)) {
      if (Array.isArray(fieldErrors)) {
        fieldErrors.forEach(error => {
          // Format: "Field name: error message" or just "error message" for base errors
          if (field === 'base') {
            messages.add(error)
          } else {
            // For nested errors like "report_sheets[0].title", extract a cleaner message
            // Just show the error without the technical field path
            messages.add(error)
          }
        })
      }
    }

    // If no messages were extracted, show a generic error
    if (messages.size === 0) {
      messages.add('שגיאה בשמירת הדוח')
    }

    // Create and insert flash message for each unique error
    messages.forEach(message => {
      this.insertFlashMessage(message, 'error')
    })
  }

  insertFlashMessage(message, type = 'error') {
    // Find the flash container (fixed at top of viewport)
    const flashContainer = document.querySelector('#flash-container')
    if (!flashContainer) {
      logger.error('[Auto-Save] Could not find #flash-container for flash message')
      return
    }

    // Map flash types to Bootstrap alert classes (matching app/views/shared/_flash.slim)
    const typeMap = {
      'notice': 'alert-success',
      'success': 'alert-success',
      'alert': 'alert-warning',
      'warning': 'alert-warning',
      'error': 'alert-danger',
      'danger': 'alert-danger',
      'info': 'alert-info'
    }

    const alertClass = typeMap[type] || 'alert-info'

    // Create flash message HTML using the existing flash partial structure
    // This matches the output of app/views/shared/_flash.slim
    const flashHtml = `
      <div class="alert ${alertClass} alert-dismissible fade show"
           role="alert"
           data-controller="flash"
           data-flash-type-value="${type}">
        ${this.escapeHtml(message)}
        <button type="button" class="btn-close" data-action="flash#close" aria-label="Close"></button>
      </div>
    `

    // Insert into the flash container (will appear at top of screen)
    flashContainer.insertAdjacentHTML('beforeend', flashHtml)
  }

  escapeHtml(unsafe) {
    return unsafe
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#039;")
  }

  /**
   * Captures column IDs marked for destruction BEFORE a save starts.
   * This is critical to prevent a race condition where columns deleted DURING
   * a save get prematurely removed from the DOM before being sent to the server.
   *
   * @param {HTMLFormElement} form - The form element
   * @returns {Set<string>} Set of column IDs that are marked for destruction
   */
  captureColumnsMarkedForDestruction(form) {
    const columnIds = new Set()

    const selectedColumnsList = form.querySelector('#selectedColumnsList')
    if (!selectedColumnsList) return columnIds

    const destroyFields = selectedColumnsList.querySelectorAll('input[name*="[_destroy]"][value="true"]')

    destroyFields.forEach(field => {
      const columnItem = field.closest('.selected-column-item-v3')
      if (columnItem) {
        const columnId = columnItem.dataset.columnId || columnItem.id
        columnIds.add(columnId)
      }
    })

    logger.log(`[Auto-Save] Captured ${columnIds.size} columns for cleanup: ${Array.from(columnIds).join(', ')}`)
    return columnIds
  }

  /**
   * Removes columns from DOM after a successful save.
   * Only removes columns that were captured BEFORE the save started, preventing
   * premature removal of columns deleted during the save.
   *
   * @param {Set<string>} columnsToCleanup - Set of column IDs to remove (captured before save)
   */
  cleanupDestroyedRecords(columnsToCleanup) {
    // Find all column items marked for destruction and remove them from the DOM
    // This prevents RecordNotFound errors when Rails tries to find already-deleted records
    const form = this.element.tagName === 'FORM' ? this.element : this.element.querySelector('form')
    if (!form) return

    // Only look in the selected columns list, NOT in the available columns library
    const selectedColumnsList = form.querySelector('#selectedColumnsList')
    if (!selectedColumnsList) return

    // If no columns were captured, nothing to cleanup
    if (!columnsToCleanup || columnsToCleanup.size === 0) {
      logger.log(`[Auto-Save] No columns to cleanup`)
      return
    }

    logger.log(`[Auto-Save] Cleaning up ${columnsToCleanup.size} columns that were saved`)

    // Only remove columns that were captured BEFORE the save started
    columnsToCleanup.forEach(columnId => {
      const columnItem = selectedColumnsList.querySelector(`[data-column-id="${columnId}"]`)
      if (columnItem) {
        logger.log(`[Auto-Save] Removing destroyed column from DOM: ${columnId}`)
        columnItem.remove()
      }
    })

    // CRITICAL: After removing destroyed columns, also clean up any orphaned input fields
    // that might have been left behind by Rails fields_for
    // These appear as numeric-indexed fields (e.g., report_columns_attributes][0][id])
    this.cleanupOrphanedFields(selectedColumnsList)
  }

  cleanupOrphanedFields(container) {
    // Find all input fields within report_columns_attributes
    const allColumnFields = container.querySelectorAll('input[name*="[report_columns_attributes]"]')

    // Group fields by their column index/id
    const fieldGroups = new Map()

    allColumnFields.forEach(field => {
      // Extract the column index/id from the field name
      // Examples:
      //   report[report_sheets_attributes][400][report_columns_attributes][5218][title]
      //   report[report_sheets_attributes][400][report_columns_attributes][0][id]
      const match = field.name.match(/\[report_columns_attributes\]\[([^\]]+)\]/)
      if (match) {
        const columnKey = match[1]
        if (!fieldGroups.has(columnKey)) {
          fieldGroups.set(columnKey, [])
        }
        fieldGroups.get(columnKey).push(field)
      }
    })

    // Check each group - if all fields are orphaned (no parent column item), remove them
    fieldGroups.forEach((fields, columnKey) => {
      // Check if any of these fields has a parent .selected-column-item-v3
      const hasParent = fields.some(field => field.closest('.selected-column-item-v3') !== null)

      if (!hasParent) {
        logger.log(`[Auto-Save] Cleaning up ${fields.length} orphaned fields for column ${columnKey}`)
        fields.forEach(field => field.remove())
      }
    })
  }

  recalculateColumnPositions() {
    // Find the selected columns list
    const selectedList = document.querySelector('#selectedColumnsList')
    if (!selectedList) {
      return
    }

    // Get all column items in DOM order (this is the visual order)
    const columnItems = selectedList.querySelectorAll('.selected-column-item-v3')

    // Update position field for each column based on its VISIBLE position in the DOM
    // Skip columns marked for destruction (hidden) so positions are contiguous
    let visibleIndex = 0
    columnItems.forEach((item) => {
      const destroyField = item.querySelector('input[name*="[_destroy]"]')
      const isDestroyed = destroyField && destroyField.value === 'true'

      const positionInput = item.querySelector('input[name*="[position]"]')
      if (positionInput) {
        if (isDestroyed) {
          // Keep destroyed columns at a high position so they sort last
          // (doesn't matter much since they'll be deleted)
          positionInput.value = 99999
        } else {
          positionInput.value = visibleIndex
          visibleIndex++
        }
      }
    })

    logger.log(`[Auto-Save] Recalculated positions for ${visibleIndex} visible columns (${columnItems.length} total)`)
  }
}

